
import { getNumberOption } from './utils.js'

/**
 * `ObjectCache` is a LRU cache designed to automatically remove entries and to remove itself when the object is garbage collected.
 */
export default class ObjectCache {
  static #weakMap = new WeakMap()
  static MAX_CACHE_ENTRIES = 1000

  static get description () {
    return (
      'ObjectCache is a LRU cache designed to automatically ' +
      'remove entries and to remove itself when the object ' +
      'is garbage collected.'
    )
  }

  static get (obj) {
    return this.#weakMap.get(obj)
  }

  /**
   *
   * @param {object} obj
   * @param {{ maxSize?: number }?} options
   * @returns {ObjectCache}
   */
  static from (obj, options) {
    let cache = this.#weakMap.get(obj)

    if (!cache) {
      cache = new ObjectCache(options)
      this.#weakMap.set(obj, cache)
    }

    return cache
  }

  #map = new Map()
  #now
  #maxSize = ObjectCache.MAX_CACHE_ENTRIES
  #calls = 0
  #hits = 0
  #misses = 0

  /**
   *
   * @param {Array<Array<K extends any, V extends any>> | undefined} entries
   * @param {{ maxSize?: number } | undefined} options
   * @returns {ObjectCache}
   */
  constructor (entries, options) {
    this.#now = globalThis.performance ? globalThis.performance.now() : Date.now()
    options = options === undefined ? {} : Object.create(options)

    this.#maxSize = getNumberOption(
      options,
      'maxSize',
      5,
      Number.MAX_VALUE,
      ObjectCache.MAX_CACHE_ENTRIES
    )

    if (entries !== undefined && entries !== null) {
      let size = 0

      try {
        entries = [...entries]
        let index = entries.length

        while (--index >= 0 && ++size <= this.maxSize) {
          this.#map.set(...entries[index])
        }
      } catch (err) {
        throw new RangeError(`Unable to clone entries: ${entries} into cache.`)
      }
    }
  }

  get maxSize () {
    return this.#maxSize
  }

  get size () {
    return this.#map.size
  }

  entries () {
    return this.#map.entries()
  }

  keys () {
    return this.#map.keys()
  }

  clear () {
    return this.#map.clear()
  }

  get (key) {
    ++this.#calls
    const result = this.#map.get(key)

    if (result !== undefined) {
      ++this.#hits
      this.#map.delete(key)
      this.#map.set(key, result)
    } else {
      ++this.#misses
    }

    return result
  }

  set (key, value) {
    if (!this.#map.has(key) && this.size === this.maxSize) {
      const entries = this.#map.entries()
      const entry = entries.next().value
      this.#map.delete(entry[0])
    }

    this.#map.set(key, value)
    return this
  }

  report () {
    if (this.#calls === 0) return
    const ms = (globalThis.performance ? globalThis.performance.now() : Date.now()) - this.#now
    const hitRate = ((100 * this.#hits) / this.#calls).toFixed(0)
    console.log(`${this.#calls} calls in ${ms.toFixed(2)}ms. Hits: ${this.#hits} (${hitRate}%). Misses: ${this.#misses}.`)
  }
}
